import { TextField } from "components/fields/base/TextField";
import { css, cx } from "../../../styled-system/css";
import { Flex } from "../../../styled-system/jsx";
import signUpLogo from "assets/images/logo/Trellis_Logo.png";
import PhoneInputWithCountrySelect, { isValidPhoneNumber } from "react-phone-number-input";
import { Label } from "react-aria-components";
import { inputClassNames } from "components/fields/styles/styles";
import { Select } from "components/fields/base/Select";
import { Checkbox } from "components/fields/base/Checkbox";
import { Button } from "components/core/basic/Button";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useState } from "react";
import TermsOfService from "../../components/custom/instructions/TermsOfService";
import { getAPIKeys } from "utils/getUrlPrefix";
import { useMutation } from "@tanstack/react-query";
import setAuthToken from "utils/setAuthToken";
import { setMarketPlace } from "redux/reducers/marketPlace";
import { setChannel } from "redux/reducers/channel";
import { setBypassOnboarding, setCurrentUser } from "redux/reducers/userReducer";
import { setTokens } from "redux/reducers/authReducers";
import { trackMixpanelEvent } from "hooks/useMixpanelTracking";
import { trackGaEvent } from "utils/trackGaEvent";
import { trackLinkedinPixelEvent } from "utils/trackLinkedinPixelEvent";
import { trackFacebookPixelEvent } from "utils/trackFacebookPixelEvent";
import { useAppDispatch } from "redux/hooks";
import { Link, useHistory } from "react-router-dom";
import { HoverInfo } from "components/core/basic/Tooltip";
import { apiClient } from "zodios/client";

const apiKeys = getAPIKeys();

const CLIENT_ID = apiKeys["API_AUTH_CLIENT_ID"];
const CLIENT_SECRET = apiKeys["API_AUTH_SECRET"];

interface RegisterFormData {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  password: string;
  phoneNumber: string;
  howDidYouHear: string | null;
  agreedToTerms: boolean;
}

const INITIAL_DATA: RegisterFormData = {
  firstName: "",
  lastName: "",
  companyName: "",
  email: "",
  password: "",
  phoneNumber: "",
  howDidYouHear: null,
  agreedToTerms: false,
};

const SignUpForm = ({
  extensionSignup = false,
  onLoginClick,
}: {
  extensionSignup: boolean;
  onLoginClick: () => void;
}) => {
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  const history = useHistory();
  const dispatch = useAppDispatch();

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    mode: "onBlur",
    defaultValues: INITIAL_DATA,
  });

  const formState = useWatch({ control });

  const classes = inputClassNames();

  const createOrgMutation = useMutation({
    mutationFn: async ({
      tokens,
      formData,
    }: {
      tokens: { access_token: string; refresh_token: string };
      formData: RegisterFormData;
    }) => {
      const signUpFormData = {
        user_email: formData.email.trim(),
        organization_name: formData.companyName.trim(),
        referral_token: "",
        affiliate_token: "",
        country: "",
        specified_user_email: formData.email.trim(),
        contact_number: formData.phoneNumber.trim(),
        hear_about_us: formData.howDidYouHear,
        terms_agreement: formData.agreedToTerms,
        bypass_onboarding: new Date().toISOString().slice(0, 10),
      };

      await apiClient.registerOrganization(signUpFormData);
      const { access_token: accessToken } = tokens;

      localStorage.setItem("Tokens", JSON.stringify(tokens));

      const authRes = await apiClient.fetchLoginUserInfo({
        custom_api_request: "login_user_info",
        user_email: formData.email.trim(),
      });

      setAuthToken(accessToken);
      dispatch(setMarketPlace("USA"));
      dispatch(setChannel("amazon"));
      dispatch(setCurrentUser(authRes));
      dispatch(setTokens(tokens));
      dispatch(setBypassOnboarding(new Date().toISOString().slice(0, 10)));

      localStorage.setItem("Marketplace", "USA");
      localStorage.setItem("channel", "amazon");
      localStorage.setItem("redirect", "/onboarding/v2/about/type");

      trackMixpanelEvent("Click", {
        name: "Sign Up",
        element: "button",
        extension_user: !!localStorage.extensionSignup,
        ...signUpFormData,
      });
      trackGaEvent(localStorage.extensionSignup ? "Extension User Sign Up" : "App User Sign Up", "Signup");
      trackLinkedinPixelEvent();
      trackFacebookPixelEvent(localStorage.extensionSignup ? "Extension User Sign Up" : "App User Sign Up");

      history.push("/onboarding/v2/about/type");
    },
  });

  const createUserAccountMutation = useMutation({
    mutationFn: async (values: RegisterFormData) => {
      return await apiClient.signupUser({
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        password: values.password,
      });
    },
    onSuccess: async (data, vars) => {
      const loginRes: { access_token: string; refresh_token: string } = await apiClient.login({
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        grant_type: "password",
        username: vars.email,
        password: vars.password,
      });

      if (loginRes.access_token && loginRes.refresh_token) {
        const { access_token, refresh_token } = loginRes;
        createOrgMutation.mutate({ tokens: { access_token, refresh_token }, formData: vars });
      }
    },
    onError: (res: { response: { data: { email?: string[]; password?: string[] } } }) => {
      const errors = res.response.data;

      if (errors.email && errors.email[0]) {
        setError("email", { type: "custom", message: errors.email[0] });
      }

      if (errors.password && errors.password[0]) {
        setError("password", { type: "custom", message: errors.password[0] });
      }
    },
  });

  const onSubmit = (values: RegisterFormData) => {
    createUserAccountMutation.mutate(values);
  };

  return (
    <Flex
      className={css({
        maxWidth: "100%",
        height: "100%",
        paddingBottom: "large",
      })}
      direction={"column"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      <img
        src={signUpLogo}
        alt={"Trellis logo"}
        className={css({
          width: "300px",
          paddingY: "xlarge",
        })}
      />
      <p>The beginning of your Trellis experience starts now.</p>
      <form
        id={"AppSignUpForm"}
        className={cx(
          css({
            display: "flex",
            gap: "large",
            flexDirection: "column",
            width: { base: "100%", md: "580px", lg: "620px" },
            padding: { base: "xlarge", lg: "inherit" },
          }),
          "App Sign-Up Form",
        )}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Flex
          className={css({
            gap: "large",
            flexDirection: { base: "column", sm: "row" },
          })}
        >
          <Controller
            name={"firstName"}
            control={control}
            rules={{ required: { value: true, message: "First name is required" } }}
            render={({ field, fieldState }) => (
              <TextField
                label={"First Name"}
                type={"text"}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name={"lastName"}
            control={control}
            rules={{ required: { value: true, message: "Last name is required" } }}
            render={({ field, fieldState }) => (
              <TextField
                label={"Last Name"}
                type={"text"}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </Flex>
        <Controller
          name={"companyName"}
          control={control}
          rules={{ required: { value: true, message: "A company name is required" } }}
          render={({ field, fieldState }) => (
            <TextField
              label={"Company Name"}
              type={"text"}
              onChange={(value) => field.onChange(value)}
              onBlur={field.onBlur}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name={"email"}
          control={control}
          rules={{
            required: { value: true, message: "An email is required" },
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              label={"Email"}
              type={"email"}
              onChange={(value) => field.onChange(value)}
              onBlur={field.onBlur}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name={"password"}
          control={control}
          rules={{ required: { value: true, message: "A password is required" } }}
          render={({ field, fieldState }) => (
            <TextField
              label={
                <span className={css({ display: "flex", gap: "small" })}>
                  Password{" "}
                  <HoverInfo iconClassName={css({ color: "grey", fontSize: "small" })}>
                    Password must be at least 8 characters long and include at least one uppercase letter, one lowercase
                    letter, and one number.
                  </HoverInfo>
                </span>
              }
              type={"password"}
              onChange={(value) => field.onChange(value)}
              onBlur={field.onBlur}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <div>
          <Controller
            name={"phoneNumber"}
            control={control}
            rules={{
              required: { value: true, message: "A phone number is required" },
              validate: (value) => isValidPhoneNumber(value) || "Please enter a valid phone number",
            }}
            render={({ field, fieldState }) => (
              <>
                <Label className={classes.label}>Phone Number</Label>
                <PhoneInputWithCountrySelect
                  className={`phone_number_input ${css({ border: "1px solid #DDD", borderRadius: "2000000px", marginBottom: 0, paddingX: "large" })}`}
                  international
                  withCountryCallingCode
                  placeholder="Contact Number"
                  onChange={(value) => field.onChange(value)}
                  onBlur={field.onBlur}
                />
                {fieldState.error && (
                  <p className={`${classes.error} ${css({ paddingY: "small" })}`}>{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>
        <Controller
          name={"howDidYouHear"}
          control={control}
          rules={{ required: { value: true, message: "This field is required" } }}
          render={({ field, fieldState }) => (
            <Select
              label={"How Did You Hear About Us"}
              onSelectionChange={(value) => {
                field.onChange(value);
              }}
              onBlur={field.onBlur}
              selectedKey={formState.howDidYouHear}
              items={[
                { label: "Facebook", value: "Facebook" },
                { label: "Google", value: "Google" },
                { label: "Bing", value: "Bing" },
                { label: "Reddit", value: "Reddit" },
                { label: "WhatsApp", value: "WhatsApp" },
                { label: "Referral", value: "Referral" },
                { label: "YouTube", value: "YouTube" },
                { label: "Amazon Ads Directory", value: "Amazon Ads Directory" },
              ]}
              errorMessage={fieldState.error?.message}
            />
          )}
        />

        <Flex
          className={css({ paddingY: "small", fontSize: "medium", whiteSpace: "nowrap" })}
          justifyContent={"center"}
        >
          <Checkbox
            value={formState.agreedToTerms as boolean}
            onChange={(checked) => setValue("agreedToTerms", checked)}
            className={css({ marginRight: "medium" })}
          />
          I agree to the{" "}
          <span
            className={css({ paddingX: "xsmall", color: "trellisPalette.0", fontWeight: "bold", whiteSpace: "nowrap" })}
            onClick={() => setShowTermsOfService(!showTermsOfService)}
          >
            Terms of Use
          </span>{" "}
          and{" "}
          <a
            href={"https://gotrellis.com/privacy-policy/"}
            target={"_blank"}
            className={css({
              paddingX: "xsmall",
              color: "trellisPalette.0",
              textDecoration: "none",
              fontWeight: "bold",
            })}
            onClick={() => setShowPrivacyPolicy(!showPrivacyPolicy)}
            rel="noreferrer"
          >
            Privacy Policy
          </a>
        </Flex>
        <Button
          type={"submit"}
          className={css({ marginX: "auto", paddingX: "100px", fontSize: "larger", textTransform: "none" })}
          isDisabled={!!Object.values(formState).some((value) => !value) || !!Object.keys(errors).length}
          isPending={createUserAccountMutation.isPending || createOrgMutation.isPending}
        >
          {extensionSignup ? "Connect Extension" : "Sign Up"}
        </Button>
        <div className={css({ marginY: "small", fontSize: "small", textAlign: "center" })}>
          Already a member?{" "}
          <Link
            to={"/"}
            onClick={() => onLoginClick()}
            className={css({
              cursor: "pointer",
              color: "trellisPalette.0",
              textDecoration: "underline",
              fontWeight: "normal",
            })}
          >
            Sign In
          </Link>
        </div>
      </form>

      {showTermsOfService ?
        <TermsOfService justTerms={"showTerms"} closeTerms={() => setShowTermsOfService(false)} />
      : null}
    </Flex>
  );
};

export default SignUpForm;
